import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { emptyFilter, type RiftboundCard, type RiftboundFilter } from '../../types';
import { useCollection } from '../../hooks/useCollection';
// buildPrintPdf is dynamically imported inside downloadPdf() — it pulls in
// jsPDF (~350 kB), which must not ride along in the eager app shell.
import { riftboundToaster } from '../../components/ui/toaster';
import { DEFAULT_GRID_SORT } from '../../constants';
import { getApiBaseUrl } from '../../constants/environment';
import { fetchCardPrintsCached } from '../../utils/cardPrintCache';
import type { PaperFormat, TrayLine } from '../../features/card-print/printPdfLayout';
import {
  buildCardSearchParams,
  deckCardRefsAllPrintZones,
  deckMainDeckTrayRequests,
} from '../../utils/cardUtils';
import { deckCardRefToRiftboundCard } from '../../utils/deckCardRef';
import type { Deck } from '../../types/deck';
import { recordSupportFeatureEngagement } from '../../utils/supportModalTracking';
import { AnalyticsEvents, trackEvent } from '../../analytics/events';

export const CARD_PRINT_PAGE_SIZE = 30;

/** Fixed strip above the tab bar when the tray has items (mobile). */
export const MOBILE_CARD_PRINT_TRAY_STRIP_HEIGHT_PX = 56;

function buildPrintCardsUrl(
  filters: RiftboundFilter,
  searchTerm: string,
  sort: string,
  page: number
): string {
  const merged: RiftboundFilter = { ...emptyFilter, ...filters };
  const params = buildCardSearchParams(merged, searchTerm, sort, page, CARD_PRINT_PAGE_SIZE);
  return `${getApiBaseUrl()}/api/cards?${params.toString()}`;
}

export type TrayMap = Record<string, TrayLine>;

export interface CardPrintContextValue {
  searchTerm: string;
  setSearchTerm: (v: string) => void;
  filters: RiftboundFilter;
  setFilters: React.Dispatch<React.SetStateAction<RiftboundFilter>>;
  sort: string;
  setSort: (v: string) => void;
  cards: RiftboundCard[];
  total: number;
  loading: boolean;
  error: string | null;
  hasMore: boolean;
  tray: TrayMap;
  trayTotalQty: number;
  trayLines: TrayLine[];
  setTrayQty: (card: RiftboundCard, qty: number) => void;
  trayQty: (id: string) => number;
  clearTray: () => void;
  cutMode: boolean;
  setCutMode: (v: boolean) => void;
  bleedMode: boolean;
  setBleedMode: (v: boolean) => void;
  cmykMode: boolean;
  setCmykMode: (v: boolean) => void;
  paperFormat: PaperFormat;
  setPaperFormat: (f: PaperFormat) => void;
  pdfLoading: boolean;
  downloadPdf: () => Promise<boolean>;
  importFromDeck: (deck: Deck, options?: { missingOnly?: boolean }) => Promise<void>;
  importFromDeckLoading: string | null;
  loadMore: () => Promise<void>;
}

export const CardPrintContext =
  createContext<CardPrintContextValue | null>(null);

export function CardPrintProvider({ children }: { children: ReactNode }) {
  const { entries } = useCollection();

  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<RiftboundFilter>(() => ({ ...emptyFilter }));
  const [sort, setSort] = useState(DEFAULT_GRID_SORT);

  const [cards, setCards] = useState<RiftboundCard[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [tray, setTray] = useState<TrayMap>({});

  const [cutMode, setCutMode] = useState(false);
  const [bleedMode, setBleedMode] = useState(false);
  const [cmykMode, setCmykMode] = useState(false);
  const [paperFormat, setPaperFormat] = useState<PaperFormat>('a4');

  const [pdfLoading, setPdfLoading] = useState(false);
  const [importFromDeckLoading, setImportFromDeckLoading] = useState<string | null>(null);

  const queryRef = useRef('');
  const loadMoreInFlightRef = useRef(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(searchTerm.trim()), 350);
    return () => window.clearTimeout(t);
  }, [searchTerm]);

  const queryKey = useMemo(
    () => JSON.stringify({ debouncedSearch, filters, sort }),
    [debouncedSearch, filters, sort]
  );

  useEffect(() => {
    const key = queryKey;
    queryRef.current = key;
    loadMoreInFlightRef.current = false;
    setLoading(true);
    setError(null);
    setPage(1);

    (async () => {
      try {
        const url = buildPrintCardsUrl(filters, debouncedSearch, sort, 1);
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (queryRef.current !== key) return;
        setCards(data.cards ?? []);
        const pg = data.pagination;
        setTotal(typeof pg?.total === 'number' ? pg.total : (data.cards?.length ?? 0));
        setHasMore(
          Boolean(pg?.hasMore ?? (data.cards?.length ?? 0) >= CARD_PRINT_PAGE_SIZE)
        );
      } catch (err) {
        if (queryRef.current !== key) return;
        setError(err instanceof Error ? err.message : 'Failed to load cards');
        setCards([]);
        setTotal(0);
        setHasMore(false);
      } finally {
        if (queryRef.current === key) setLoading(false);
      }
    })();
  }, [queryKey, debouncedSearch, filters, sort]);

  const loadMore = useCallback(async () => {
    if (loadMoreInFlightRef.current || loading || !hasMore) return;
    loadMoreInFlightRef.current = true;
    const nextPage = page + 1;
    const key = queryRef.current;
    setLoading(true);
    try {
      const url = buildPrintCardsUrl(filters, debouncedSearch, sort, nextPage);
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (queryRef.current !== key) return;
      setCards(prev => [...prev, ...(data.cards ?? [])]);
      setPage(nextPage);
      const pg = data.pagination;
      if (typeof pg?.total === 'number') setTotal(pg.total);
      setHasMore(
        Boolean(pg?.hasMore ?? (data.cards?.length ?? 0) >= CARD_PRINT_PAGE_SIZE)
      );
    } catch (err) {
      riftboundToaster.create({
        title: 'Load error',
        description: err instanceof Error ? err.message : 'Failed to load more cards',
        type: 'error',
      });
    } finally {
      loadMoreInFlightRef.current = false;
      if (queryRef.current === key) setLoading(false);
    }
  }, [loading, hasMore, page, filters, debouncedSearch, sort]);

  const setTrayQty = useCallback((card: RiftboundCard, qty: number) => {
    const n = Math.max(0, Math.min(999, Math.floor(qty)));
    setTray(prev => {
      if (n === 0) {
        const next = { ...prev };
        delete next[card.id];
        return next;
      }
      return { ...prev, [card.id]: { card, qty: n } };
    });
  }, []);

  const trayQty = useCallback((id: string) => tray[id]?.qty ?? 0, [tray]);
  const trayLines = useMemo(() => Object.values(tray).filter(t => t.qty > 0), [tray]);
  const trayTotalQty = useMemo(() => trayLines.reduce((s, t) => s + t.qty, 0), [trayLines]);

  const clearTray = useCallback(() => setTray({}), []);

  const importFromDeck = useCallback(async (deck: Deck, options?: { missingOnly?: boolean }) => {
    const missingOnly = Boolean(options?.missingOnly);
    const mergedRefs = deckCardRefsAllPrintZones(deck);
    const requests = deckMainDeckTrayRequests(mergedRefs, entries, missingOnly);

    if (!requests.length) {
      riftboundToaster.create({
        title: missingOnly ? 'Nothing missing' : 'Empty deck',
        description: missingOnly
          ? 'You already own every card in this deck.'
          : 'This deck has no printable cards yet (library, champion, legend, battlefields, or sideboard).',
        type: 'info',
      });
      return;
    }

    const cardIds = requests.map((r) => r.cardId);
    const refById = new Map(mergedRefs.map((c) => [c.cardId, c]));
    const qtyById = new Map(requests.map((req) => [req.cardId, req.qty]));

    setImportFromDeckLoading(deck.id);
    setTray({});

    const buildTrayFromRefs = (): TrayMap => {
      const next: TrayMap = {};
      for (const id of cardIds) {
        const ref = refById.get(id);
        const qty = qtyById.get(id);
        if (!ref || qty == null || qty <= 0) continue;
        const card = deckCardRefToRiftboundCard(ref);
        next[card.id] = { card, qty };
      }
      return next;
    };

    const idsNeedingImages = cardIds.filter((id) => {
      const ref = refById.get(id);
      return ref && !ref.imageUrl?.trim() && !ref.thumbnailUrl?.trim();
    });

    try {
      const next = buildTrayFromRefs();

      if (idsNeedingImages.length > 0) {
        const fetched = await fetchCardPrintsCached(idsNeedingImages);
        for (const card of fetched) {
          const qty = qtyById.get(card.id);
          if (qty == null || qty <= 0) continue;
          next[card.id] = { card, qty };
        }
      }

      if (!Object.keys(next).length) {
        throw new Error('No cards could be loaded from this deck.');
      }

      setTray(next);
    } catch (err) {
      riftboundToaster.create({
        title: 'Import failed',
        description: err instanceof Error ? err.message : 'Failed to import deck',
        type: 'error',
      });
    } finally {
      setImportFromDeckLoading(null);
    }
  }, [entries]);

  const downloadPdf = useCallback(async (): Promise<boolean> => {
    if (!trayLines.length) {
      riftboundToaster.create({
        title: 'Tray empty',
        description: 'Add cards to the tray first.',
        type: 'warning',
      });
      return false;
    }
    setPdfLoading(true);
    try {
      const { buildPrintPdf } = await import('../../features/card-print/buildPrintPdf');
      const blob = await buildPrintPdf(trayLines, { cutMode, bleedMode, cmykMode, paperFormat });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `riftbound-proxies-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      recordSupportFeatureEngagement(`proxy-sheet-pdf:${Date.now()}`);
      trackEvent(AnalyticsEvents.PROXY_DOWNLOAD);
      return true;
    } catch (err) {
      riftboundToaster.create({
        title: 'PDF export failed',
        description: err instanceof Error ? err.message : 'Could not build PDF',
        type: 'error',
      });
      return false;
    } finally {
      setPdfLoading(false);
    }
  }, [trayLines, cutMode, bleedMode, cmykMode, paperFormat]);

  const value = useMemo<CardPrintContextValue>(
    () => ({
      searchTerm,
      setSearchTerm,
      filters,
      setFilters,
      sort,
      setSort,
      cards,
      total,
      loading,
      error,
      hasMore,
      tray,
      trayTotalQty,
      trayLines,
      setTrayQty,
      trayQty,
      clearTray,
      cutMode,
      setCutMode,
      bleedMode,
      setBleedMode,
      cmykMode,
      setCmykMode,
      paperFormat,
      setPaperFormat,
      pdfLoading,
      downloadPdf,
      importFromDeck,
      importFromDeckLoading,
      loadMore,
    }),
    [
      searchTerm,
      filters,
      sort,
      cards,
      total,
      loading,
      error,
      hasMore,
      tray,
      trayTotalQty,
      trayLines,
      setTrayQty,
      trayQty,
      clearTray,
      cutMode,
      bleedMode,
      cmykMode,
      paperFormat,
      pdfLoading,
      downloadPdf,
      importFromDeck,
      importFromDeckLoading,
      loadMore,
    ]
  );

  return (
    <CardPrintContext.Provider value={value}>
      {children}
    </CardPrintContext.Provider>
  );
}
